import { memo, useCallback, useMemo } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useI18n } from "@/i18n/I18nProvider";

const WRAPPER_SX = { mt: 2, bgcolor: "transparent" };

const SUMMARY_SX = {
  px: 0,
  py: { xs: 1.5, md: 3 },
  gap: 1,
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-content": { my: 0 },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
};

const DETAILS_SX = { px: { xs: 2, md: 3 }, pt: 0, pb: 2 };
const ANSWER_SX = { lineHeight: 1.7, fontSize: { md: 15 } };
const QUESTION_SX = { fontWeight: 700, fontSize: { xs: 16, md: 18 } };

function getStableKey(item, index) {
  // Prefer a stable id if you have it
  return item?.id ?? item?.key ?? item?.q ?? index;
}

function ListAccordion({
  items = [],
  renderHTML = false,
  replaceValues = {},
  withBg = false,
  variant,
}) {
  const { locale } = useI18n();
  const isArabic = locale === "ar";

  const replacePlaceholders = useCallback(
    (text) => {
      if (!text) return "";
      const entries = Object.entries(replaceValues);
      if (entries.length === 0) return text;

      let result = text;
      for (const [key, value] of entries) {
        result = result.replaceAll(`{{${key}}}`, String(value ?? ""));
      }
      return result;
    },
    [replaceValues]
  );

  // Precompute resolved q/a once instead of doing it during render loop each time
  const resolvedItems = useMemo(() => {
    return items.map((item, index) => ({
      ...item,
      __key: getStableKey(item, index),
      __q: replacePlaceholders(item?.q),
      __a: replacePlaceholders(item?.a),
    }));
  }, [items, replacePlaceholders]);

  const expandIcon = useMemo(() => {
    const sx = { color: "#f27a21", fontSize: 40 };
    return isArabic ? <ChevronLeftIcon sx={sx} /> : <ChevronRightIcon sx={sx} />;
  }, [isArabic]);

  const accordionSx = useMemo(
    () => ({
      borderBottom: withBg
        ? "0.1px solid #fff"
        : "1px solid rgba(0,0,0,0.2)",
      "&:before": { display: "none" },
      bgcolor: "transparent",
      color: withBg ? "#fff" : undefined,
    }),
    [withBg]
  );

  return (
    <Box sx={WRAPPER_SX} data-aos="fade-up" data-aos-delay="500">
      {resolvedItems.map((item) => (
        <Accordion key={item.__key} elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={expandIcon} sx={SUMMARY_SX}>
            <Typography sx={QUESTION_SX} variant={variant ?? "h4"}>
              {item.__q}
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={DETAILS_SX}>
            {renderHTML ? (
              <Typography
                sx={ANSWER_SX}
                variant="body1"
                dangerouslySetInnerHTML={{ __html: item.__a }}
              />
            ) : (
              <Typography sx={ANSWER_SX} variant="body1">
                {item.__a}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default memo(ListAccordion);
